import { useEffect, useRef } from 'react';

interface Options {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  rootMargin?: string;
}

/**
 * Returns a ref to attach to a sentinel element. When it scrolls into view
 * (and hasMore && !loading), invokes onLoadMore. Caller controls debouncing
 * by gating onLoadMore behind their own loading flag.
 */
export function useInfiniteScroll<T extends HTMLElement>({
  hasMore,
  loading,
  onLoadMore,
  rootMargin = '200px',
}: Options) {
  const ref = useRef<T | null>(null);
  const cbRef = useRef(onLoadMore);
  cbRef.current = onLoadMore;

  useEffect(() => {
    const node = ref.current;
    if (!node || !hasMore) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e?.isIntersecting && !loading) cbRef.current();
      },
      { rootMargin }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasMore, loading, rootMargin]);

  return ref;
}
